import { APP_BASE_HREF, PlatformLocation } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import {
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    LOCALE_ID,
    provideZoneChangeDetection
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule, REMOVE_STYLES_ON_COMPONENT_DESTROY } from "@angular/platform-browser";
import { RouteReuseStrategy, TitleStrategy } from "@angular/router";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { provideTranslateService } from "@ngx-translate/core";
import { msalProviders } from "./app-msal.config";
import { AppRoutingModule } from "./app-routing.module";
import { BusyService } from "./common/components/busy/busy.service";
import { VerksamhetshandelseService } from "./common/components/loggning/verksamhetshandelse.service";
import { MapModule } from "./common/components/map/map.module";
import { ModalComponentMediator } from "./common/components/modal/modal-component-mediator";
import { RaskModalService } from "./common/components/modal/rask-modal.service";
import { ShowroomModule } from "./common/components/showroom/showroom.module";
import { ToastService } from "./common/components/toast/toast.service";
import { CustomInterceptor } from "./common/custom-interceptor";
import { ErrorInterceptor } from "./common/error-interceptor";
import { RaskCommonModule } from "./common/rask-common.module";
import { AnvandareService } from "./common/services/anvandare.service";
import { ApiClientService } from "./common/services/api-client.service";
import { AppInsightsService } from "./common/services/app-insights.service";
import { AuthenticationService } from "./common/services/authentication/authentication.service";
import { DirtyFormService } from "./common/services/dirty-form.service";
import { EventHubService } from "./common/services/eventhub.service";
import { FeatureService } from "./common/services/feature.service";
import { FormatService } from "./common/services/format.service";
import { MaskinlagService } from "./common/services/maskinlag.service";
import { EnvironmentTitleStrategy } from "./environment-title-strategy";
import { AnvandareModule } from "./features/administration/anvandare/anvandare.module";
import { CertifikatModule } from "./features/administration/certifikat/certifikat.module";
import { JobbModule } from "./features/administration/jobb/jobb.module";
import { KontraktsmigreringModule } from "./features/administration/kontraktsmigrering/kontraktsmigrering.module";
import { MattslagskonverteringModule } from "./features/administration/mattslagskonvertering/mattslagskonvertering.module";
import { MattslagskonverteringService } from "./features/administration/mattslagskonvertering/mattslagskonvertering.service";
import { OvervakningModule } from "./features/administration/overvakning/overvakning.module";
import { ProduktionsomradeModule } from "./features/administration/produktionsomrade/produktionsomrade.module";
import { RebusModule } from "./features/administration/rebus/rebus.module";
import { RegelverkViol3Module } from "./features/administration/regelverk-viol3/regelverkViol3.module";
import { SortimentModule } from "./features/administration/sortiment/sortiment.module";
import { SystemadministrationModule } from "./features/administration/systemadministration/systemadministration.module";
import { SystemaktivitetModule } from "./features/administration/systemaktivitet/systemaktivitet.module";
import { ForsaljningModule } from "./features/forsaljning/forsaljning.module";
import { HanteraLagermalModule } from "./features/hantera-lagermal/hantera-lagermal.module";
import { JusteraLeveransplanModule } from "./features/justera-leveransplan/justera-leveransplan.module";
import { LeveransModule } from "./features/Leverans/leverans.module";
import { KartgeometriPopupModule } from "./features/map/popups/kartgeometri-popup/kartgeometri-popup.module";
import { ArbetsorderPlaneringSchedulerModule } from "./features/planera/arbetsorder-planering-scheduler/arbetsorder-planering-scheduler.module";
import { ArbetsorderProduktvalModule } from "./features/planera/arbetsorder-produktval/arbetsorder-produktval.module";
import { ArbetsorderVirkesorderModule } from "./features/planera/arbetsorder-virkesorder/arbetsorder-virkesorder.module";
import { PlaneraModule } from "./features/planera/planera.module";
import { ProduktionsmallarModule } from "./features/planera/produktionsmallar/produktionsmallar.module";
import { ArbetsorderService } from "./features/planera/services/arbetsorder.service";
import { AtgardPlaneringsstatusService } from "./features/planera/services/atgard-planeringsstatus.service";
import { ProduktionModule } from "./features/produktion/produktion.module";
import { ExampleModule } from "./features/rask-components-example/example.module";
import { Viol3formatService } from "./features/shared/services/viol3format.service";
import { SharedModule } from "./features/shared/shared.module";
import { TransportModule } from "./features/transport/transport.module";
import { VirkeskopngxModule } from "./features/virkeskop/virkeskop.module";
import { LeverantorDialogService } from "./features/virkeskop/virkesleverantor/dialog/leverantor-dialog.service";
import { VirkesorderModule } from "./features/virkesorder/virkesorder.module";

import { HOT_GLOBAL_CONFIG } from "@handsontable/angular-wrapper";
import { DASHBOARD_CONFIG } from "./common/components/dashboard/dashboard-config";
import { globalHotConfig } from "./handsontable-config";
import { PageModule } from "./pages/page.module";
import { RaskDashboardConfig } from "./rask-dashboard-config";
import { RaskRouteReuseStrategy } from "./rask-route-reuse-strategy";
import { SeleniumModule } from "./selenium/selenium.module";
import { translateModuleConfig } from "./translate-module.config";

const getBaseHref = (platformLocation: PlatformLocation): string => platformLocation.getBaseHrefFromDOM();

export const appConfig: ApplicationConfig = {
    providers: [
        provideTranslateService(translateModuleConfig),
        importProvidersFrom(
            AppRoutingModule,
            FormsModule,
            ReactiveFormsModule,
            BrowserModule,
            NgbModule,
            ExampleModule,
            MapModule,
            OvervakningModule, JobbModule,
            RebusModule,
            RegelverkViol3Module,
            SortimentModule, CertifikatModule,
            KontraktsmigreringModule,
            VirkesorderModule,
            AnvandareModule,
            ShowroomModule,
            JusteraLeveransplanModule,
            SystemaktivitetModule,
            PlaneraModule, SharedModule,
            ArbetsorderProduktvalModule,
            ArbetsorderVirkesorderModule,
            ProduktionsmallarModule,
            TransportModule,
            ArbetsorderPlaneringSchedulerModule,
            ForsaljningModule,
            ProduktionModule,
            HanteraLagermalModule,
            PageModule,
            MapModule,
            MattslagskonverteringModule,
            SystemadministrationModule,
            ProduktionsomradeModule,
            KartgeometriPopupModule,
            LeveransModule,
            VirkeskopngxModule,
            RaskCommonModule,
            SeleniumModule
        ),
        ...msalProviders,
        ArbetsorderService,
        AtgardPlaneringsstatusService,
        EventHubService,
        RaskModalService,
        AnvandareService,
        ModalComponentMediator,
        ApiClientService,
        DirtyFormService,
        BusyService,
        FeatureService,
        FormatService,
        AuthenticationService,
        VerksamhetshandelseService,
        LeverantorDialogService,
        ToastService,
        MaskinlagService,
        AppInsightsService,
        MattslagskonverteringService,
        Viol3formatService,
        {
            provide: LOCALE_ID,
            useValue: "sv"
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CustomInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: ErrorInterceptor
        },
        {
            provide: REMOVE_STYLES_ON_COMPONENT_DESTROY,
            useValue: false
        },
        {
            provide: APP_BASE_HREF,
            useFactory: getBaseHref,
            deps: [PlatformLocation]
        },
        {
            provide: TitleStrategy,
            useClass: EnvironmentTitleStrategy
        },
        {
            provide: RouteReuseStrategy,
            useClass: RaskRouteReuseStrategy
        },
        {
            provide: HOT_GLOBAL_CONFIG,
            useValue: globalHotConfig
        },
        {
            provide: DASHBOARD_CONFIG,
            useValue: RaskDashboardConfig
        },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideZoneChangeDetection({ eventCoalescing: true })
    ]
};
